import React, { useEffect, useState } from "react";
import {
  Smartphone,
  Clock,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  History,
  Search,
  ChevronDown,
  X,
  Users,
  Store,
  CalendarDays,
  Download,
  Eye,
  Repeat,
  RotateCcw,
  UserCheck,
} from "lucide-react";
import axios from "../../utils/axiosDash";

function servicePeriod(time) {
  const hour = Number(time.split(":")[0]);
  if (hour < 11) return "Breakfast service";
  if (hour < 15) return "Lunch service";
  if (hour < 18) return "Afternoon lull";
  if (hour < 22) return "Peak service";
  return "Late night service";
}

const STATUS_META = {
  flagged: { label: "Flagged", chip: "bg-red-50 text-red-700 ring-1 ring-red-200", dot: "bg-red-500" },
  review: { label: "Under Review", chip: "bg-amber-50 text-amber-700 ring-1 ring-amber-200", dot: "bg-amber-500" },
  escalated: { label: "Escalated", chip: "bg-rose-100 text-rose-800 ring-1 ring-rose-300", dot: "bg-rose-600" },
};

const PRIORITY_META = {
  Low: "bg-sky-400",
  Medium: "bg-amber-400",
  High: "bg-rose-500",
};

function formatDuration(sec) {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function initials(name) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

const AVATAR_COLORS = [
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-violet-100 text-violet-700",
];

function avatarColor(name) {
  const sum = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

// ---------------------------------------------------------------------------

export default function CaptainBehaviourDashboard() {
  const [incidents, setIncidents] = useState([]);
  const [stores, setStores] = useState([]);
  const [actionOptions, setActionOptions] = useState([]);
  const [tab, setTab] = useState("feed"); // feed | store | captain
  const [storeFilter, setStoreFilter] = useState("All Stores");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);

  // Per-incident acknowledgment state, keyed by incident id
  const [ackMap, setAckMap] = useState({});

  const enriched = incidents.map((i) => ({
    ...i,
    acknowledged: ackMap[i.id]?.acknowledged || false,
    actionTaken: ackMap[i.id]?.actionTaken || "No action yet",
    ackTime: ackMap[i.id]?.ackTime || null,
  }));

  const fetchCaptainBehaviour = async () => {
    try {
      const storeName =
        localStorage.getItem("selectedStore") || "All Stores";

      const response = await axios.post(
        "/get-empire-captain-behaviour",
        {
          storeName,
        }
      );

      if (response.data?.success) {
        const data = response.data.data || {};

        setIncidents(data.incidents || []);
        setStores(data.stores || []);
        setActionOptions(data.actionOptions || []);
      }
    } catch (error) {
      console.error(
        "Error fetching captain behaviour:",
        error
      );
    }
  };

  useEffect(() => {
    fetchCaptainBehaviour();
  }, []);

  function acknowledge(id, actionTaken = "Spoke to the staff member") {
    setAckMap((prev) => ({
      ...prev,
      [id]: {
        acknowledged: true,
        actionTaken,
        ackTime: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      },
    }));
  }

  function reopen(id) {
    setAckMap((prev) => ({
      ...prev,
      [id]: { ...prev[id], acknowledged: false, ackTime: null },
    }));
  }

  function setActionTaken(id, actionTaken) {
    setAckMap((prev) => ({
      ...prev,
      [id]: { ...prev[id], actionTaken },
    }));
  }

  const filtered = enriched.filter((i) => {
    const matchStore = storeFilter === "All Stores" || i.store === storeFilter;
    const matchStatus = statusFilter === "All Statuses" || i.status === statusFilter;
    const matchQuery =
      query.trim() === "" || i.captain.toLowerCase().includes(query.trim().toLowerCase());
    return matchStore && matchStatus && matchQuery;
  });

  const activeIncident = enriched.find((i) => i.id === activeId) || null;

  const totalCaptains = new Set(incidents.map((i) => i.captain)).size;
  const totalIncidents = incidents.length;
  const totalDuration = incidents.reduce((a, i) => a + i.duration, 0);
  const repeatOffenders = new Set(incidents.filter((i) => i.seq >= 2).map((i) => i.captain)).size;
  const acknowledgedCount = enriched.filter((i) => i.acknowledged).length;

  const storeGroups = stores.map((store) => ({
    store,
    count: incidents.filter((i) => i.store === store).length,
    duration: incidents
      .filter((i) => i.store === store)
      .reduce((a, i) => a + Number(i.duration || 0), 0),
  })).sort((a, b) => b.count - a.count);

  const captainNames = [...new Set(incidents.map((i) => i.captain))];
  const captainGroups = captainNames
    .map((name) => {
      const rows = incidents.filter((i) => i.captain === name);
      return {
        name,
        store: rows[0].store,
        count: rows.length,
        duration: rows.reduce((a, i) => a + i.duration, 0),
        lastSeen: rows[0],
      };
    })
    .sort((a, b) => b.count - a.count);

  const maxStoreCount = Math.max(...storeGroups.map((s) => s.count), 1);
  const maxCaptainCount = Math.max(...captainGroups.map((c) => c.count), 1);

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Page heading */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-teal-600">Operations · Compliance</p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Captain Behaviour Monitoring
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Mobile phone usage detected across store CCTV feeds, by captain and location.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50">
              <CalendarDays size={16} className="text-slate-400" />
              Last 7 days
              <ChevronDown size={14} className="text-slate-400" />
            </button>
            <button className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700">
              <Download size={16} />
              Export
            </button> */}
          </div>
        </div>

        {/* KPI cards */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <KpiCard
            icon={<Users size={18} className="text-teal-600" />}
            label="Total Captains Detected"
            value={totalCaptains}
            hint="Unique captains on camera this period"
          />
          <KpiCard
            icon={<Smartphone size={18} className="text-red-600" />}
            label="Mobile Usage Incidents"
            value={totalIncidents}
            hint="Flagged across all stores"
            accent="red"
          />
          <KpiCard
            icon={<Clock size={18} className="text-amber-600" />}
            label="Total Duration on Phone"
            value={formatDuration(totalDuration)}
            hint="Summed across all incidents"
            accent="amber"
          />
          <KpiCard
            icon={<Repeat size={18} className="text-rose-600" />}
            label="Repeat Offenders"
            value={repeatOffenders}
            hint="Captains with 2+ violations"
            accent="rose"
          />
          <KpiCard
            icon={<UserCheck size={18} className="text-teal-600" />}
            label="Acknowledged"
            value={`${acknowledgedCount}/${totalIncidents}`}
            hint="Reviewed by a manager"
          />
        </div>

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-1 border-b border-slate-200">
          <TabButton active={tab === "feed"} onClick={() => setTab("feed")}>
            Incident Feed
          </TabButton>
          <TabButton active={tab === "store"} onClick={() => setTab("store")}>
            Store-wise
          </TabButton>
          <TabButton active={tab === "captain"} onClick={() => setTab("captain")}>
            Captain-wise
          </TabButton>
        </div>

        {/* Incident feed */}
        {tab === "feed" && (
          <div className="mt-5">
            {/* Filters */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1 sm:max-w-xs">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search captain name"
                  className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100"
                />
              </div>
              {/* <Dropdown
                value={storeFilter}
                onChange={setStoreFilter}
                options={["All Stores", ...stores]}
                icon={<Store size={14} className="text-slate-400" />}
              /> */}
              <Dropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={["All Statuses", "flagged", "review", "escalated"]}
                labels={{ flagged: "Flagged", review: "Under Review", escalated: "Escalated" }}
                icon={<AlertTriangle size={14} className="text-slate-400" />}
              />
              <span className="ml-auto text-sm text-slate-400">
                {filtered.length} incident{filtered.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Incident cards */}
            <div className="mt-4 space-y-3">
              {filtered.map((incident) => (
                <IncidentRow
                  key={incident.id}
                  incident={incident}
                  onView={() => setActiveId(incident.id)}
                  onAcknowledge={() => acknowledge(incident.id)}
                  onReopen={() => reopen(incident.id)}
                />
              ))}
              {filtered.length === 0 && (
                <div className="rounded-xl border border-dashed border-slate-200 bg-white py-14 text-center text-sm text-slate-400">
                  No incidents match these filters.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Store-wise */}
        {tab === "store" && (
          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {storeGroups.map((s) => (
              <div key={s.store} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                      <Store size={16} />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{s.store}</p>
                      <p className="text-xs text-slate-400">{formatDuration(s.duration)} total on phone</p>
                    </div>
                  </div>
                  <span className="text-lg font-semibold text-slate-900">{s.count}</span>
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-teal-500"
                    style={{ width: `${(s.count / maxStoreCount) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Captain-wise */}
        {tab === "captain" && (
          <div className="mt-5 space-y-3">
            {captainGroups.map((c) => (
              <div
                key={c.name}
                className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${avatarColor(c.name)}`}>
                    {initials(c.name)}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900">{c.name}</p>
                      {c.count >= 2 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
                          <History size={11} /> Repeat
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400">{c.store} · last seen {c.lastSeen.date} at {c.lastSeen.time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 sm:w-64">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-rose-500"
                      style={{ width: `${(c.count / maxCaptainCount) * 100}%` }}
                    />
                  </div>
                  <span className="w-24 text-right text-sm text-slate-600">
                    {c.count} incident{c.count !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evidence / alert detail modal */}
      {activeIncident && (
        <EvidenceModal
          incident={activeIncident}
          onClose={() => setActiveId(null)}
          onAcknowledge={(action) => acknowledge(activeIncident.id, action)}
          onReopen={() => reopen(activeIncident.id)}
          onActionChange={(action) => setActionTaken(activeIncident.id, action)}
          actionOptions={actionOptions}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function KpiCard({ icon, label, value, hint, accent }) {
  const ring =
    accent === "red"
      ? "ring-red-100"
      : accent === "amber"
      ? "ring-amber-100"
      : accent === "rose"
      ? "ring-rose-100"
      : "ring-teal-100";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-50 ring-1 ${ring}`}>
        {icon}
      </div>
      <p className="mt-3 text-2xl font-semibold text-slate-900">{value}</p>
      <p className="text-sm font-medium text-slate-600">{label}</p>
      <p className="mt-0.5 text-xs text-slate-400">{hint}</p>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
        active ? "text-teal-700" : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
      {active && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-teal-600" />}
    </button>
  );
}

function Dropdown({ value, onChange, options, labels, icon }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-8 pr-8 text-sm text-slate-600 shadow-sm focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {labels?.[opt] ?? opt}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">{icon}</span>
      <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

function IncidentRow({ incident, onView, onAcknowledge, onReopen }) {
  const meta = STATUS_META[incident.status];
  return (
    <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center">
      {/* Thumbnail */}
      <button
        onClick={onView}
        className="group relative h-20 w-full shrink-0 overflow-hidden rounded-lg bg-slate-900 sm:w-32"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
        <span className="absolute bottom-1 left-1.5 rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white">
          {incident.time}
        </span>
      </button>

      {/* Details */}
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${avatarColor(incident.captain)}`}>
            {initials(incident.captain)}
          </span>
          <p className="text-sm font-medium text-slate-900">{incident.captain}</p>
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${meta.chip}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
          {incident.seq >= 2 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              <History size={11} /> {incident.seq === 2 ? "2nd violation" : `${incident.seq}th violation`}
            </span>
          )}
          {incident.acknowledged && (
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700 ring-1 ring-teal-200">
              <CheckCircle2 size={11} /> Acknowledged{incident.ackTime ? ` · ${incident.ackTime}` : ""}
            </span>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <Smartphone size={12} className="text-slate-400" /> Mobile detected
          </span>
          <span className="inline-flex items-center gap-1">
            <Store size={12} className="text-slate-400" /> {incident.store}
          </span>
          <span className="inline-flex items-center gap-1">
            <MapPin size={12} className="text-slate-400" /> {incident.camera}
          </span>
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={12} className="text-slate-400" /> {incident.date} · {incident.time}
          </span>
          <span className="inline-flex items-center gap-1 font-medium text-slate-700">
            <Clock size={12} className="text-slate-400" /> {formatDuration(incident.duration)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex shrink-0 items-center gap-2 self-start sm:self-center">
        <button
          onClick={onView}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          <Eye size={14} />
          View details
        </button>
        {incident.acknowledged ? (
          <button
            onClick={onReopen}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100"
          >
            <RotateCcw size={14} />
            Reopen
          </button>
        ) : (
          <button
            onClick={onAcknowledge}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-teal-700"
          >
            <CheckCircle2 size={14} />
            Acknowledge
          </button>
        )}
      </div>
    </div>
  );
}

// A stylised "CCTV still" — illustrative shapes standing in for a real frame,
// with a bounding box on the flagged captain, matching the reference layout.
function SnapshotIllustration({ incident }) {
  return (
    <div className="relative aspect-video overflow-hidden rounded-xl bg-slate-900">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-950" />

      {/* camera header bar */}
      <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-black/40 px-3 py-2 text-[11px] text-white/80">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
          CAM {incident.camNo} · {incident.camera}
        </span>
        <span>{incident.date.split("-").reverse().join("-")} · {incident.time}:07</span>
      </div>

      {/* illustrated hall: a few round tables with figures */}
      <div className="absolute inset-0 flex items-center justify-around px-6 pb-2 pt-8">
        <Figure />
        <Figure small />
        <div className="relative">
          <Figure highlighted />
          <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-amber-400 px-2 py-1 text-[10px] font-semibold text-slate-900">
            Phone in use {formatDuration(incident.duration)} · {incident.confidence}%
          </span>
        </div>
        <Figure small />
      </div>

      <p className="absolute bottom-2 left-3 right-3 text-[10px] leading-snug text-white/50">
        Illustrated snapshot for this demonstration — the real deployment shows the actual CCTV
        still, with a short clip around the event.
      </p>
    </div>
  );
}

function Figure({ small, highlighted }) {
  const height = small ? "h-10" : "h-14";
  return (
    <div className={`relative flex flex-col items-center ${highlighted ? "z-10" : ""}`}>
      {highlighted && (
        <span className="absolute -inset-2 rounded-md border-2 border-amber-400" />
      )}
      <div className={`w-6 rounded-full bg-slate-400/70 ${height}`} />
      <div className="-mt-2 h-3 w-3 rounded-full bg-slate-300/80" />
      <div className="mt-3 h-1.5 w-14 rounded-full bg-slate-600/60" />
    </div>
  );
}

function EvidenceModal({
  incident,
  onClose,
  onAcknowledge,
  onReopen,
  onActionChange,
  actionOptions,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-slate-900 text-slate-100 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 pt-5">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Phone in use {formatDuration(incident.duration)}
            </h3>
            <p className="mt-0.5 text-sm text-slate-400">Captain behaviour, {incident.time}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <SnapshotIllustration incident={incident} />
        </div>

        <dl className="mt-4 divide-y divide-slate-800 px-5 text-sm">
          <DetailRow label="Priority">
            <span className="inline-flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${PRIORITY_META[incident.priority]}`} />
              {incident.priority}
            </span>
          </DetailRow>
          <DetailRow label="Time">{incident.time}</DetailRow>
          <DetailRow label="Captain">{incident.captain}</DetailRow>
          <DetailRow label="Section">{incident.section}</DetailRow>
          <DetailRow label="On phone for">{formatDuration(incident.duration)}</DetailRow>
          <DetailRow label="Service period">{servicePeriod(incident.time)}</DetailRow>
          <DetailRow label="Tables in section">{incident.tables}</DetailRow>
          <DetailRow label="Camera">CAM {incident.camNo}, {incident.camera}</DetailRow>
          <DetailRow label="Outlet">
            {incident.outlet}
          </DetailRow>
        </dl>

        <div className="px-5 py-4">
          <label className="mb-1.5 block text-xs font-medium text-slate-400">Action taken</label>
          <div className="relative">
            <select
              value={incident.actionTaken}
              onChange={(e) => onActionChange(e.target.value)}
              className="w-full appearance-none rounded-lg border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            >
              {actionOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-800 px-5 py-4">
          <button className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2.5 text-xs font-medium text-slate-300 hover:bg-slate-800">
            <Download size={14} /> Download clip
          </button>
          {incident.acknowledged ? (
            <button
              onClick={onReopen}
              className="inline-flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-medium text-amber-400 hover:bg-amber-500/20"
            >
              <RotateCcw size={16} />
              Reopen alert
            </button>
          ) : (
            <button
              onClick={() => onAcknowledge(incident.actionTaken === "No action yet" ? "Spoke to the staff member" : incident.actionTaken)}
              className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-200"
            >
              <CheckCircle2 size={16} />
              Acknowledge
            </button>
          )}
        </div>
        {incident.acknowledged && incident.ackTime && (
          <p className="px-5 pb-5 text-xs text-slate-500">Acknowledged today at {incident.ackTime}.</p>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <dt className="text-slate-400">{label}</dt>
      <dd className="font-medium text-slate-100">{children}</dd>
    </div>
  );
}